import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import { notificationsService } from '../../services/supabaseService';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deliveryStats, setDeliveryStats] = useState({ pending: 0, delivered: 0, failed: 0, total: 0 });
  const dropdownRef = useRef(null);
  const retryIntervalRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    loadDeliveryStats();

    // Subscribe to real-time notifications
    const notificationSubscription = notificationsService?.subscribeToNotifications((payload) => {
      if (payload?.eventType === 'INSERT') {
        const newNotification = payload?.new;
        setNotifications(prev => [newNotification, ...prev]);
        if (!newNotification?.isRead) {
          setUnreadCount(prev => prev + 1);
        }
        loadDeliveryStats();
      } else if (payload?.eventType === 'UPDATE') {
        const updatedNotification = payload?.new;
        setNotifications(prev => prev?.map(n => n?.id === updatedNotification?.id ? updatedNotification : n));
        loadUnreadCount();
        loadDeliveryStats();
      } else if (payload?.eventType === 'DELETE') {
        const deletedId = payload?.old?.id;
        setNotifications(prev => prev?.filter(n => n?.id !== deletedId));
        loadUnreadCount();
        loadDeliveryStats();
      }
    });

    // Subscribe to delivery status updates
    const deliverySubscription = notificationsService?.subscribeToDeliveryStatus((payload) => {
      const updatedNotification = payload?.new;
      setNotifications(prev => prev?.map(n => 
        n?.id === updatedNotification?.id ? updatedNotification : n
      ));
      loadDeliveryStats();
    });

    // Start retry processor (runs every 5 minutes)
    retryIntervalRef.current = setInterval(() => {
      processRetries();
    }, 5 * 60 * 1000);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);

    return () => {
      notificationsService?.unsubscribe(notificationSubscription);
      notificationsService?.unsubscribe(deliverySubscription);
      if (retryIntervalRef?.current) {
        clearInterval(retryIntervalRef?.current);
      }
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsService?.getAll();
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await notificationsService?.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadDeliveryStats = async () => {
    try {
      const stats = await notificationsService?.getDeliveryStats();
      setDeliveryStats(stats);
    } catch (error) {
      console.error('Error loading delivery stats:', error);
    }
  };

  const processRetries = async () => {
    try {
      const result = await notificationsService?.processRetries();
      if (result?.processed > 0) {
        console.log(`Processed ${result?.processed} retries: ${result?.succeeded} succeeded, ${result?.failed} failed`);
        loadNotifications();
        loadDeliveryStats();
      }
    } catch (error) {
      console.error('Error processing retries:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsService?.markAsRead(notificationId);
      setNotifications(prev => prev?.map(n => n?.id === notificationId ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService?.markAllAsRead();
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationsService?.delete(notificationId);
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      loadUnreadCount();
      loadDeliveryStats();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleRetryDelivery = async (notificationId) => {
    try {
      const success = await notificationsService?.attemptDelivery({ id: notificationId });
      if (success) {
        await notificationsService?.markAsDelivered(notificationId);
        loadNotifications();
        loadDeliveryStats();
      } else {
        await notificationsService?.markAsFailed(notificationId, 'Manual retry failed');
        loadNotifications();
      }
    } catch (error) {
      console.error('Error retrying delivery:', error);
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-error/10 border-error text-error';
      case 'high':
        return 'bg-warning/10 border-warning text-warning';
      case 'medium':
        return 'bg-accent/10 border-accent text-accent';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return 'AlertTriangle';
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getDeliveryStatusBadge = (status, retryCount, maxRetries) => {
    switch (status) {
      case 'delivered':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Icon name="CheckCircle" size={12} />
            Delivered
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <Icon name="XCircle" size={12} />
            Failed ({retryCount}/{maxRetries})
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Icon name="Clock" size={12} />
            Pending {retryCount > 0 ? `(Retry ${retryCount})` : ''}
          </span>
        );
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'violation_created':
        return 'AlertCircle';
      case 'violation_edited':
        return 'Edit';
      case 'point_credit_applied':
        return 'Award';
      case 'threshold_exceeded':
        return 'AlertTriangle';
      case 'compliance_drop':
        return 'TrendingDown';
      case 'critical_status':
        return 'AlertTriangle';
      default:
        return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-muted transition-smooth"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-error rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-[28rem] bg-card rounded-lg elevation-lg z-dropdown max-h-[36rem] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground">
                Notifications
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">
                  {deliveryStats?.delivered} delivered
                </span>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-yellow-600">
                  {deliveryStats?.pending} pending
                </span>
                {deliveryStats?.failed > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-red-600">
                      {deliveryStats?.failed} failed
                    </span>
                  </>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-primary hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Icon name="Bell" size={48} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground text-center">
                  No notifications yet
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    className={`p-4 transition-smooth hover:bg-muted/50 ${
                      !notification?.isRead ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-lg border ${getPriorityStyles(notification?.priority)}`}>
                        <Icon name={notification?.eventType ? getEventTypeIcon(notification?.eventType) : getPriorityIcon(notification?.priority)} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-caption font-semibold text-sm text-foreground">
                            {notification?.title}
                          </h4>
                          {!notification?.isRead && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                          {notification?.message}
                        </p>
                        
                        {/* Delivery Status Badge */}
                        <div className="mb-2">
                          {getDeliveryStatusBadge(
                            notification?.deliveryStatus,
                            notification?.retryCount,
                            notification?.maxRetries
                          )}
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(notification?.createdAt)}
                          </span>
                          <div className="flex items-center gap-2">
                            {!notification?.isRead && (
                              <button
                                onClick={() => handleMarkAsRead(notification?.id)}
                                className="text-xs text-primary hover:underline"
                              >
                                Mark read
                              </button>
                            )}
                            {notification?.deliveryStatus === 'failed' && (
                              <button
                                onClick={() => handleRetryDelivery(notification?.id)}
                                className="text-xs text-warning hover:underline"
                              >
                                Retry
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification?.id)}
                              className="text-xs text-error hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;